import type { Maybe } from '../../../common/types';

/**
 * Classification type for Jira search intent.
 */
export const KnowledgeDiscoveryJiraSearchClassification = {
	GNIN: 'GNIN',
	FPS: 'FPS',
} as const;

export type KnowledgeDiscoveryJiraSearchClassificationType =
	(typeof KnowledgeDiscoveryJiraSearchClassification)[keyof typeof KnowledgeDiscoveryJiraSearchClassification];

/**
 * Experience type for Jira search intent.
 */
export const KnowledgeDiscoveryJiraSearchExperience = {
	QUICK_FIND: 'QUICK_FIND',
} as const;

export type KnowledgeDiscoveryJiraSearchExperienceType =
	(typeof KnowledgeDiscoveryJiraSearchExperience)[keyof typeof KnowledgeDiscoveryJiraSearchExperience];

/**
 * Model type for Jira search intent.
 */
export const KnowledgeDiscoveryJiraIntentDetectionModel = {
	GNIN_HEURISTIC: 'GNIN_HEURISTIC',
	FPS_HEURISTIC: 'FPS_HEURISTIC',
} as const;

export type KnowledgeDiscoveryJiraIntentDetectionModelType =
	(typeof KnowledgeDiscoveryJiraIntentDetectionModel)[keyof typeof KnowledgeDiscoveryJiraIntentDetectionModel];

/**
 * Search classification result containing the detected type.
 */
export type KnowledgeDiscoveryJiraSearchClassificationData = {
	/** The detected search classification type. */
	type?: Maybe<KnowledgeDiscoveryJiraSearchClassificationType>;
};

/**
 * Successful intent detection result.
 */
export type KnowledgeDiscoveryJiraIntentDetection = {
	/** The search classification containing detection results. */
	searchClassification: KnowledgeDiscoveryJiraSearchClassificationData;
};

/**
 * GraphQL error type for query failures.
 */
export type QueryError = {
	message?: Maybe<string>;
};

/**
 * Union type result - either success or error.
 */
export type KnowledgeDiscoveryJiraIntentDetectionResult =
	| ({
			__typename: 'KnowledgeDiscoveryJiraIntentDetection';
	  } & KnowledgeDiscoveryJiraIntentDetection)
	| ({ __typename: 'QueryError' } & QueryError);

/**
 * GraphQL response wrapper.
 */
export type KnowledgeDiscoveryIntentDetectionResponse = {
	knowledgeDiscovery: {
		jiraIntentDetection?: Maybe<KnowledgeDiscoveryJiraIntentDetectionResult>;
	};
};

/**
 * Query variables for intent detection.
 */
export type JiraIntentDetectionQueryVariables = {
	/** The Jira site ID. */
	siteId: string;
	/** The organization ID. */
	orgId: string;
	/** The analytics session ID. */
	sessionId?: string;
	/** The search query string. */
	query: string;
	/** The user experience type. */
	experience: KnowledgeDiscoveryJiraSearchExperienceType;
	/** Model identifier for ML model selection. */
	model: KnowledgeDiscoveryJiraIntentDetectionModelType;
};
